import { Spinner, SpinnerSize, MessageBar, MessageBarType, Link, IconButton } from "@fluentui/react";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import mammoth from "mammoth";

import { getHeaders } from "../../api";
import { getToken, useLogin } from "../../authConfig";
import styles from "./DocxViewer.module.css";

interface DocxViewerProps {
    src: string;
}

/**
 * Renders a .docx citation as inline HTML in the analysis panel.
 *
 * Browsers can't natively render Word documents in an <iframe> — the response triggers
 * a download instead. This component fetches the .docx (with the user's auth header,
 * since /content is a protected route), runs it through mammoth.js to convert the body
 * to HTML, and renders that. Tables, headings, lists, and bold/italic carry over;
 * complex Word features (embedded images, comments, footnotes) are simplified or dropped.
 */
export const DocxViewer: React.FC<DocxViewerProps> = ({ src }) => {
    const [html, setHtml] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const { t } = useTranslation();
    const client = useLogin ? useMsal().instance : undefined;

    useEffect(() => {
        const fetchAndConvert = async () => {
            try {
                const token = client ? await getToken(client) : undefined;
                const response = await fetch(src, {
                    method: "GET",
                    headers: await getHeaders(token)
                });
                if (!response.ok) {
                    throw new Error(`Failed to load document (HTTP ${response.status}).`);
                }
                const arrayBuffer = await response.arrayBuffer();
                const result = await mammoth.convertToHtml({ arrayBuffer });
                setHtml(result.value);
            } catch (e: any) {
                setError(e instanceof Error ? e : new Error(String(e)));
            } finally {
                setIsLoading(false);
            }
        };
        fetchAndConvert();
    }, [src]);

    if (isLoading) {
        return (
            <div className={`${styles.loading} ${styles.docxViewer}`}>
                <Spinner size={SpinnerSize.large} label="Loading document" />
            </div>
        );
    }

    if (error) {
        return (
            <div className={`${styles.error} ${styles.docxViewer}`}>
                <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
                    {error.message}
                    <Link href={src} download>
                        Download the file
                    </Link>
                </MessageBar>
            </div>
        );
    }

    return (
        <div>
            <IconButton
                className={styles.downloadButton}
                style={{ color: "black" }}
                iconProps={{ iconName: "Save" }}
                title={t("tooltips.save")}
                ariaLabel={t("tooltips.save")}
                href={src}
                download
            />
            <div className={`${styles.docx} ${styles.docxViewer}`} dangerouslySetInnerHTML={{ __html: html }} />
        </div>
    );
};
